import { computed, observable, action, autorun, when } from 'mobx';
import objectPath from 'object-path';

import { AppRegistry } from 'core';
import { analytics } from 'core/analytics';
import { disableLeaving, allowLeaving } from 'core/routing';

import { FormStore } from 'libs/forms/app';

import { clientStore } from 'apps/client/app';
import { ContributionFileApp } from 'apps/repository/app';

import MarkdownIt from 'markdown-it';

import { conf } from 'apps/readme/config.yaml';

export class ReadmeSection {
  @observable title;
  @observable body;
  firstReaction = true;
  constructor(
    readme,
    title = '',
    body = '',
    placeholder = '',
    index = '',
    readOnly = true,
  ) {
    this.title = title;
    this.body = body;
    this.placeholder = placeholder;
    const inSpace = objectPath.get(readme, 'parent.repository.inSpace');
    if (inSpace) {
      this.inSpace = inSpace;
    } else {
      console.warn("Couldn't set space because README has no parent");
    }
    const sectionId = `readme-section-${index}`;
    conf.fields.body.readOnly = readOnly;
    conf[sectionId] = conf.sectionForm;
    this.form = new FormStore(this, conf, sectionId);
  }
}

export class ReadmeApp extends ContributionFileApp {
  static registryName = 'ReadmeApp';
  @observable initialized = false;
  @observable editing = false;
  @observable uploading = false;
  @observable sectionApps = [];
  @observable activeSection = 0;
  @observable isDraft = false;
  @observable projectId = '';

  constructor(parent, init) {
    super(parent, init);
    this.disposers.push(this.DisposeReadme);
    this.disposers.push(
      autorun(() => {
        if (!this.initialized) return;
        if (this.editing) {
          if (!this.sessionDraftInterval) {
            analytics.logDebug('Automatic session drafts are now enabled');
            this.sessionDraftInterval = setInterval(
              this.SaveSessionDraft,
              5000,
            );
            window.addEventListener('blur', this.SaveSessionDraft);
          }
        } else {
          if (this.sessionDraftInterval) {
            analytics.logDebug('Automatic session drafts are now disabled.');
            clearInterval(this.sessionDraftInterval);
            window.removeEventListener('blur', this.SaveSessionDraft);
          }
          if (this.isDraft) {
            this.SaveSessionDraft();
          }
        }
      }),
    );
  }

  @action setInitialized = initialized => {
    this.initialized = initialized;
  };

  async initialize(args = { content: '', sync: false }) {
    const { content, sync } = args;
    const draft = this.GetSessionDraft();
    if (draft && !this.isDraft) {
      this.RestoreSessionDraft();
      this.setInitialized(true);
      this.Edit();
    } else if (this.file) {
      await this.file.readAsText();
      if (!this.file.error && this.file.fileAsText) {
        this.parseSections(this.file.fileAsText);
      }
      this.setInitialized(true);
    } else if (content) {
      this.parseSections(content);
      if (sync) this.SaveSessionDraft();
      this.setInitialized(true);
    }
  }

  @action DisposeReadme = () => {
    if (this.editing) {
      this.editing = false;
    }
    this.disposeSections();
  };

  @computed get draftKey() {
    const originContributionId = this.origin ? this.origin.id : 'initial';
    const keyElements = [this.projectId, originContributionId, this.path];
    if (clientStore.signedInProfile) {
      keyElements.unshift(clientStore.signedInProfile.username);
    }
    return keyElements.join('-');
  }

  @action SaveSessionDraft = () => {
    if (!(this.sectionApps && this.sectionApps.length)) return;
    try {
      analytics.logDebug('Requesting save of session draft');
      const draft = this.toMarkDownString(this.sectionApps);
      // Prevent saving a 0 bytes readme a draft
      if (!draft) return;
      sessionStorage.setItem(this.draftKey, draft);
      this.isDraft = true;
    } catch (error) {
      analytics.logError(error);
    }
  };

  @action GetSessionDraft = () => {
    try {
      const serializedDraft = sessionStorage.getItem(this.draftKey);
      if (!serializedDraft) return null;
      const draft = serializedDraft;
      if (!draft) return null;
      return draft;
    } catch (error) {
      analytics.logError(error);
    }
    return null;
  };

  @action RestoreSessionDraft = () => {
    analytics.logDebug('Requesting restore of session draft');
    const draft = this.GetSessionDraft();
    if (!draft) {
      analytics.logDebug('No draft found to restore');
      return;
    }
    analytics.logDebug('Draft found. Restoring.');
    this.parseSections(draft);
    this.isDraft = true;
    if (!this.initialized) {
      this.initialized = true;
    }
  };

  @action ClearSessionDraft = () => {
    try {
      analytics.logDebug('Requesting removal of session draft');
      sessionStorage.removeItem(this.draftKey);
      this.isDraft = false;
    } catch (error) {
      analytics.logError(error);
    }
  };

  @action disposeSections = () => {
    // dispose pre-existing sections if any
    if (this.sectionApps && this.sectionApps.length) {
      this.sectionApps.forEach(section => {
        if (section.disposer) {
          section.disposer();
        }
      });
    }
  };

  @action parseSections = mdString => {
    this.disposeSections();

    // Use MarkdownIt to parse readme string into tokens
    const md = new MarkdownIt();
    md.configure({
      components: {
        core: { rules: ['block'] },
      },
    });
    const tokenizedString = md.parse(mdString);
    if (!tokenizedString.length) return;
    const headers = [];
    let header = [];
    // If there's no 'Overview' header, add a fake one
    if (
      !(
        tokenizedString &&
        tokenizedString[0].type === 'heading_open' &&
        tokenizedString[0].tag === 'h1'
      )
    ) {
      const fakeHeader = md.parse('# Overview');
      fakeHeader.map = [0, 0];
      headers.push(fakeHeader);
    }
    // Extract the headers, grouping them
    let ingest = false;
    // eslint-disable-next-line
    for (const token of tokenizedString) {
      if (token.type === 'heading_open' && ['h1', 'h2'].includes(token.tag)) {
        ingest = true;
      }
      if (ingest) header.push(token);
      if (token.type === 'heading_close' && ['h1', 'h2'].includes(token.tag)) {
        ingest = false;
        headers.push(header);
        header = [];
      }
    }
    const stringLines = mdString.split('\n');
    // FIXME - don't reuse header or headers
    // eslint-disable-next-line
    this.sectionApps = headers.map((header, i, headers) => {
      /*
       * Get the line number where the section ends.
       * If this is the last section, it'll  be the last line,
       * otherwise, it'll be the line before the next header
       */
      const lineEnd =
        i === headers.length - 1
          ? stringLines.length
          : headers[i + 1][0].map[0];
      const head = header[1].content;
      const body = stringLines.slice(header[0].map[1], lineEnd).join('\n');
      return new ReadmeSection(this, head, body, '', i, !this.editing);
    });
  };

  toMarkDownString = sectionApps => {
    const serializedSections = sectionApps.map((section, i) => {
      const title = `${i === 0 ? '#' : '##'} ${section.title}`;
      const { body } = section;
      return [title, body].join('\n\n');
    });
    return serializedSections.join('\n\n');
  };

  toMarkDownFile = sectionApps => {
    const mdString = this.toMarkDownString(sectionApps);
    const mdBlob = new Blob([mdString], {
      type: 'text/markdown',
    });
    mdBlob.name = 'README.md';
    mdBlob.lastModifiedDate = new Date();
    return { fileObject: mdBlob, fullPath: mdBlob.name };
  };

  @action SetActiveSection = index => {
    this.activeSection = index;
  };

  @action CreateSection = index => {
    const newSection = new ReadmeSection(
      this,
      '',
      '',
      '',
      index + 1,
      !this.editing,
    );
    newSection.form.fieldStores.get('body').editor.SetEditable(this.editing);
    this.sectionApps.splice(index + 1, 0, newSection);
  };

  @action DeleteSection = index => {
    this.sectionApps.splice(Number(index), 1);
  };

  @action Edit = () => {
    this.SaveSessionDraft();
    this.editing = true;
    // FIXME - use proper modal
    /* eslint-disable no-restricted-globals, no-alert */
    disableLeaving(() => {
      const confirmation = confirm(
        'You might lose unsaved changes. Do you still want to leave?',
      );
      if (confirmation) {
        this.editing = false;
      }
      return confirmation;
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const section of this.sectionApps) {
      section.form.fieldStores.get('body').editor.SetEditable(this.editing);
    }
    analytics.sendEvent('clicked-readme-edit', {}, { ga: true });
  };

  @action Cancel = () => {
    this.editing = false;
    allowLeaving();
    // eslint-disable-next-line no-restricted-syntax
    for (const section of this.sectionApps) {
      section.form.fieldStores.get('body').editor.SetEditable(this.editing);
    }
    this.ClearSessionDraft();
    this.init({ sync: true });
  };

  @action Save = () => {
    analytics.sendEvent('readme-saved-edit');
    this.uploading = true;
    this.editing = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const section of this.sectionApps) {
      section.form.fieldStores.get('body').editor.SetEditable(this.editing);
    }
    when(
      () => clientStore.uploader.fastTrack.length === 0,
      () => {
        const file = this.toMarkDownFile(this.sectionApps);
        this.manager.AddFiles([file], {
          immediate: true,
          uploadCallback: () => {
            this.uploading = false;
            allowLeaving();
            this.ClearSessionDraft();
          },
        });
      },
    );
  };

  @action
  Contribute = repo => {
    const { routerStore } = clientStore;
    const routeName = routerStore.route.name;
    const spaceType = routeName.split('.')[0];
    const routeParams = routerStore.route.params;
    clientStore.routerStore.navigate(`${routeName}.files`, {
      [spaceType]: routeParams[spaceType],
      slug: routeParams.slug,
    });
    analytics.sendEvent('readme-saved-edit');
    if (this.isDraft) {
      const file = this.toMarkDownFile(this.sectionApps);
      this.manager.AddFiles([file], {
        immediate: true,
        uploadCallback: repo.manager.ContributionForm,
      });
      allowLeaving();
      this.editing = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const section of this.sectionApps) {
        section.form.fieldStores.get('body').editor.SetEditable(this.editing);
      }
    } else {
      repo.manager.ContributionForm();
    }
  };

  hasSectionWithTitle = title => {
    for (let i = 0; i < this.sectionApps.length; i += 1) {
      if (this.sectionApps[i].title === title) {
        return true;
      }
    }

    return false;
  };

  @action setSections = sectionAppsArray => {
    this.sectionApps.length = 0;
    this.sectionApps.push(...sectionAppsArray);
  };
}
AppRegistry.add(ReadmeApp);
